import { useId } from "react";
import { CONSIGNMENT_BASES } from "@ondo/shared";

import { REVISIONS_COPY } from "./revisions.constants.ts";
import { useRevisionsController } from "./useRevisionsController.ts";
import type { RevisionsController } from "./useRevisionsController.ts";
import type { PendingRevision, SettledRevision, TermLine } from "./revisions.model.ts";
import "./revisions.css";

/**
 * A consignment's terms: what is in force, what is proposed, and who agreed.
 *
 * Mounted on BOTH SIDES of the same arrangement — inside `ConsignPanel` on a
 * work's own record, which is the owner's view, and on the gallery's inventory
 * row. One component, because the question is the same from either end and the
 * server decides which of them the reader is: `yoursToAnswer` comes off the row,
 * resolved from the reader's memberships, and the answer controls are drawn from
 * it rather than from anything computed here.
 *
 * `canAct` draws the PROPOSE control. It is the account-level capability, the
 * same hint `ConsignPanel` takes; whether this consignment may be repriced by
 * this caller is `ondo.propose_consignment_revision()`'s to refuse, and it does.
 */
export function RevisionsPanel({
  consignmentId,
  canAct,
}: {
  consignmentId: string;
  canAct: boolean;
}) {
  const c = useRevisionsController(consignmentId, canAct);
  const { view } = c;

  return (
    <section className="revisions">
      <header className="revisions__head">
        <h3 className="revisions__title">{REVISIONS_COPY.title}</h3>
        {view.kind === "terms" && view.canPropose && !c.form.open && (
          <button type="button" className="revisions__action" onClick={c.openForm}>
            {REVISIONS_COPY.propose.open}
          </button>
        )}
      </header>

      {view.kind === "loading" && <p className="revisions__note">{view.message}</p>}
      {view.kind === "unavailable" && <p className="revisions__note">{view.message}</p>}

      {view.kind === "terms" && (
        <>
          <p className="revisions__version">
            {`${REVISIONS_COPY.inForce} · ${view.inForce.label} · ${REVISIONS_COPY.since} ${view.inForce.since}`}
          </p>
          <Terms lines={view.inForce.terms} />
          <p className="revisions__note">{REVISIONS_COPY.floorNote}</p>

          {c.form.sent && (
            <p className="revisions__sent" role="status">
              {REVISIONS_COPY.propose.sent}
            </p>
          )}

          {view.pending && <Pending c={c} pending={view.pending} />}

          {c.form.open && <ProposeForm c={c} />}

          {view.historyNote && <p className="revisions__note">{view.historyNote}</p>}

          {view.history.length > 0 && (
            <div className="revisions__history">
              <h4 className="revisions__history-head">{REVISIONS_COPY.history.heading}</h4>
              {view.history.map((each) => (
                <Settled key={each.revisionId} revision={each} />
              ))}
            </div>
          )}
        </>
      )}

      {c.error && (
        <p className="revisions__note revisions__note--error" role="alert">
          {c.error}
        </p>
      )}
    </section>
  );
}

function Terms({ lines }: { lines: TermLine[] }) {
  return (
    <dl className="revisions__terms">
      {lines.map((line) => (
        <div key={line.label}>
          <dt>{line.label}</dt>
          <dd>{line.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The proposal awaiting an answer.
 *
 * The two buttons are drawn only when the server says this side owes the answer.
 * A reader looking at their OWN side's proposal gets the terms, both answers and
 * the sentence saying nothing has changed — which is the truthful state and the
 * one a proposer needs.
 */
function Pending({ c, pending }: { c: RevisionsController; pending: PendingRevision }) {
  const copy = REVISIONS_COPY.pending;

  return (
    <div className="revisions__pending">
      <p className="revisions__pending-head">{`${copy.heading} · ${pending.label}`}</p>
      <p className="revisions__provenance">{pending.provenance}</p>
      <Terms lines={pending.terms} />

      <ul className="revisions__agreement">
        {pending.agreement.map((line) => (
          <li key={line.party}>
            <span className="revisions__agreement-party">{line.party}</span>
            <span
              className={
                line.outstanding
                  ? "revisions__agreement-answer revisions__agreement-answer--outstanding"
                  : "revisions__agreement-answer"
              }
            >
              {line.answer}
            </span>
          </li>
        ))}
      </ul>

      <p className="revisions__note">{pending.note}</p>

      {pending.yours && (
        <div className="revisions__answer-actions">
          <button
            type="button"
            className="revisions__accept"
            disabled={c.answering !== null}
            onClick={() => c.answer(true)}
          >
            {c.answering === "accept" ? copy.accepting : copy.accept}
          </button>
          <button
            type="button"
            className="revisions__refuse"
            disabled={c.answering !== null}
            onClick={() => c.answer(false)}
          >
            {c.answering === "refuse" ? copy.refusing : copy.refuse}
          </button>
        </div>
      )}
    </div>
  );
}

function Settled({ revision }: { revision: SettledRevision }) {
  return (
    <div className="revisions__settled">
      <p className="revisions__version">{revision.label}</p>
      <p className="revisions__outcome">{`${revision.outcome} · ${revision.provenance}`}</p>
      <Terms lines={revision.terms} />
    </div>
  );
}

function ProposeForm({ c }: { c: RevisionsController }) {
  const { draft, problem, sending } = c.form;
  const copy = REVISIONS_COPY.propose;
  const basisId = useId();
  const curId = useId();
  const askId = useId();
  const floorId = useId();
  const noteId = useId();

  return (
    <form
      className="revisions__form"
      onSubmit={(event) => {
        event.preventDefault();
        c.propose();
      }}
    >
      <h4 className="revisions__form-title">{copy.title}</h4>
      <p className="revisions__hint">{copy.lead}</p>

      <label className="revisions__label" htmlFor={basisId}>
        {copy.basis}
      </label>
      <select
        id={basisId}
        className="revisions__input"
        value={draft.basis}
        onChange={(event) => c.setDraft({ basis: event.target.value as typeof draft.basis })}
        disabled={sending}
      >
        {CONSIGNMENT_BASES.map((each) => (
          <option key={each} value={each}>
            {REVISIONS_COPY.basis[each]}
          </option>
        ))}
      </select>

      <label className="revisions__label" htmlFor={curId}>
        {copy.currency}
      </label>
      <input
        id={curId}
        className="revisions__input revisions__input--currency"
        value={draft.currency}
        onChange={(event) => c.setDraft({ currency: event.target.value })}
        maxLength={3}
        disabled={sending}
      />

      <label className="revisions__label" htmlFor={askId}>
        {copy.asking}
      </label>
      <div>
        <input
          id={askId}
          className="revisions__input revisions__input--money"
          value={draft.asking}
          onChange={(event) => c.setDraft({ asking: event.target.value })}
          inputMode="decimal"
          disabled={sending}
        />
        <p className="revisions__hint">{copy.askingHelp}</p>
      </div>

      <label className="revisions__label" htmlFor={floorId}>
        {copy.floor}
      </label>
      <div>
        <input
          id={floorId}
          className="revisions__input revisions__input--money"
          value={draft.floor}
          onChange={(event) => c.setDraft({ floor: event.target.value })}
          inputMode="decimal"
          disabled={sending}
        />
        <p className="revisions__hint">{copy.floorHelp}</p>
      </div>

      <label className="revisions__label" htmlFor={noteId}>
        {copy.note}
      </label>
      <div>
        <textarea
          id={noteId}
          className="revisions__input revisions__input--note"
          value={draft.note}
          onChange={(event) => c.setDraft({ note: event.target.value })}
          rows={2}
          maxLength={2000}
          disabled={sending}
        />
        <p className="revisions__hint">{copy.noteHelp}</p>
      </div>

      {problem && (
        <p className="revisions__problem" role="alert">
          {problem}
        </p>
      )}

      <div className="revisions__form-actions">
        <button type="submit" className="revisions__submit" disabled={sending}>
          {sending ? copy.sending : copy.submit}
        </button>
        <button
          type="button"
          className="revisions__cancel"
          onClick={c.closeForm}
          disabled={sending}
        >
          {copy.cancel}
        </button>
      </div>
    </form>
  );
}
